import React from 'react';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SlidersHorizontal, RotateCcw } from 'lucide-react';

const DEFAULT_COLOR = { brightness: 0, contrast: 0, saturation: 0, exposure: 0, temperature: 0, tint: 0, highlights: 0, shadows: 0, filter_preset: 'none', filter_intensity: 100 };
const PRESETS = ['none', 'vivid', 'mono', 'warm', 'cool', 'vintage'];
const PRESET_LABELS = { none: 'None', vivid: 'Vivid', mono: 'Mono', warm: 'Warm', cool: 'Cool', vintage: 'Vintage' };

const Row = ({ label, value, onChange, min = -100, max = 100, approximate, testId }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between">
      <label className="text-xs text-[#8A93A6]">{label}{approximate && <span className="text-[9px] text-amber-400/70 ml-1">(preview approx.)</span>}</label>
      <span className="text-xs text-white">{Math.round(value)}</span>
    </div>
    <Slider value={[value]} min={min} max={max} step={1} onValueChange={([v]) => onChange(v)} data-testid={testId} />
  </div>
);

/**
 * Basic V1 color grade for the selected VIDEO item. Brightness/contrast/
 * saturation/temperature/tint/preset genuinely drive the live preview via
 * a documented CSS `filter` string (see EditorCompositionPreview.jsx).
 * Highlights/Shadows have no faithful CSS-only equivalent, so the live
 * preview only approximates them (flagged below) — the stored numeric
 * values remain the deterministic source of truth for the Vultr worker.
 */
export function EditorColorControls({ item, onColorChange }) {
  if (item.item_type !== 'video') return null;
  const color = { ...DEFAULT_COLOR, ...(item.color || {}) };

  return (
    <div className="space-y-3 pt-2 border-t border-white/5" data-testid="color-controls-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#555E6E] uppercase tracking-wide flex items-center gap-1.5"><SlidersHorizontal className="h-3 w-3" />Color & Filters</p>
        <Button size="sm" variant="outline" className="h-6 text-[10px] border-white/10" onClick={() => onColorChange(DEFAULT_COLOR)} data-testid="color-reset-button"><RotateCcw className="h-3 w-3 mr-1" />Reset</Button>
      </div>

      <Select value={color.filter_preset} onValueChange={(v) => onColorChange({ filter_preset: v })}>
        <SelectTrigger className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="color-preset-select"><SelectValue /></SelectTrigger>
        <SelectContent>{PRESETS.map((p) => <SelectItem key={p} value={p}>{PRESET_LABELS[p]}</SelectItem>)}</SelectContent>
      </Select>
      {color.filter_preset !== 'none' && (
        <Row label="Filter Intensity" value={color.filter_intensity} min={0} max={100} onChange={(v) => onColorChange({ filter_intensity: v })} testId="color-filter-intensity-slider" />
      )}

      <Row label="Brightness" value={color.brightness} onChange={(v) => onColorChange({ brightness: v })} testId="color-brightness-slider" />
      <Row label="Contrast" value={color.contrast} onChange={(v) => onColorChange({ contrast: v })} testId="color-contrast-slider" />
      <Row label="Saturation" value={color.saturation} onChange={(v) => onColorChange({ saturation: v })} testId="color-saturation-slider" />
      <Row label="Exposure" value={color.exposure} onChange={(v) => onColorChange({ exposure: v })} testId="color-exposure-slider" />
      <Row label="Temperature" value={color.temperature} onChange={(v) => onColorChange({ temperature: v })} testId="color-temperature-slider" />
      <Row label="Tint" value={color.tint} onChange={(v) => onColorChange({ tint: v })} testId="color-tint-slider" />
      <Row label="Highlights" value={color.highlights} onChange={(v) => onColorChange({ highlights: v })} approximate testId="color-highlights-slider" />
      <Row label="Shadows" value={color.shadows} onChange={(v) => onColorChange({ shadows: v })} approximate testId="color-shadows-slider" />
    </div>
  );
}
